<script setup>
import { ref, computed } from 'vue'
import FormButton from '@/components/Atoms/FormButton.vue'
import FormInput from '@/components/Atoms/FormInput.vue'
import { GIVING_PRESETS_MAX } from '@/components/Sites/siteTemplates'

// Suggested donation amounts as chips (1–6, kept ascending and unique — the
// paywall refuses anything else). Type an amount and press Enter or Add.
const props = defineProps({
	currency: {
		type: String,
		default: 'USD',
	},
	disabled: {
		type: Boolean,
		default: false,
	},
	error: {
		type: String,
		default: '',
	},
})
const presets = defineModel({ type: Array, required: true })

const draft = ref('')
const full = computed(() => presets.value.length >= GIVING_PRESETS_MAX)
const parsed = computed(() => {
	const n = Number(String(draft.value).replace(',', '.'))
	return Number.isFinite(n) && n > 0 ? Math.round(n * 100) / 100 : null
})
const canAdd = computed(() => !props.disabled && !full.value && parsed.value !== null && !presets.value.includes(parsed.value))

const fmt = (n) => {
	try {
		return new Intl.NumberFormat(undefined, { style: 'currency', currency: props.currency || 'USD', maximumFractionDigits: Number.isInteger(n) ? 0 : 2 }).format(n)
	} catch {
		return `${n} ${props.currency}`
	}
}
const add = () => {
	if (!canAdd.value) return
	presets.value = [...presets.value, parsed.value].sort((a, b) => a - b)
	draft.value = ''
}
const remove = (n) => { presets.value = presets.value.filter((p) => p !== n) }
</script>

<template>
	<div class="min-w-0" data-testid="giving-presets">
		<p class="mb-1 text-tiny font-medium text-surface-text-1">Suggested amounts</p>
		<div class="flex flex-wrap items-center gap-1.5" :class="presets.length ? 'mb-2' : ''" role="list">
			<span v-for="n in presets" :key="n" role="listitem" class="inline-flex items-center gap-1 rounded-full bg-surface-4 py-0.5 pl-2.5 pr-1 text-sm text-white" :data-testid="`giving-preset-${n}`">
				<span data-testid="giving-preset-amount">{{ fmt(n) }}</span>
				<button type="button" class="grid size-5 place-items-center rounded-full text-surface-8 hover:bg-surface-5 hover:text-white disabled:opacity-50" :disabled="disabled" :aria-label="`Remove ${fmt(n)}`" @click="remove(n)">×</button>
			</span>
		</div>
		<div class="flex items-end gap-2">
			<FormInput id="giving-preset-new" v-model="draft" type="number" :placeholder="full ? `Up to ${GIVING_PRESETS_MAX} amounts` : 'e.g. 25'" input-classes="placeholder:text-surface-6" :disabled="disabled || full" class="min-w-0 w-40" @keydown.enter.prevent="add" />
			<FormButton type="secondary" size="md" class="shrink-0" :disabled="!canAdd" data-testid="giving-preset-add" @click="add">Add</FormButton>
		</div>
		<p class="mb-0 mt-1.5 text-tiny" :class="error ? 'text-error' : 'text-surface-7'" data-testid="giving-presets-hint">{{ error || `1–${GIVING_PRESETS_MAX} amounts, shown as buttons on the giving form.` }}</p>
	</div>
</template>
